import { log } from '@hungryroot/o11y';

/**
 * Exp-242 home feed video QoE analytics.
 *
 * One tracker per video *view* (card activation -> teardown). Strictly
 * observer-only: never mutates the element, the hls.js instance, or playback
 * state. Emits four Datadog record types via o11y `log`, all gated by the
 * `web.feature-flag.exp-242-video-instrumentation.enabled` dynamic config
 * (see `setFeedVideoDatadogEnabled` caller in MobileReplacementHome):
 *
 * - `exp242.feedVideo.firstFrame`  — once per view, at first rendered frame
 * - `exp242.feedVideo.loop`        — one per detected loop wrap
 * - `exp242.feedVideo.error`       — at occurrence (fatal and non-fatal)
 * - `exp242.feedVideo.viewSummary` — exactly once per view, at view end
 *
 * Metric definitions, accuracy bounds, and known blind spots are documented in
 * the analytics design docs; the load-bearing choices are annotated inline.
 */

const VIDEO_STARTUP_FAILURE_TIMEOUT_MS = 8_000;

// A loop wrap is a backward currentTime jump into [0, LOOP_WRAP_MAX_START_S)
// from at least max(LOOP_WRAP_MIN_PRIOR_S, 50% of clip duration). The floor
// rejects near-zero jitter; the 50% requirement rejects short engine seeks.
const LOOP_WRAP_MAX_START_S = 1;
const LOOP_WRAP_MIN_PRIOR_S = 1;

export type FeedVideoPlaybackPath =
  | 'mse-hls-js'
  | 'native-hls'
  | 'native-progressive';

type FeedVideoErrorClass =
  | 'media_aborted'
  | 'media_network'
  | 'media_decode'
  | 'media_src_unsupported'
  | 'hls_network'
  | 'hls_media'
  | 'hls_other'
  | 'play_rejected'
  | 'startup_timeout';

type FeedVideoTtffSource = 'rvfc' | 'heuristic';

type FeedVideoOutcome =
  | 'success'
  | 'playback_failure'
  | 'startup_failure'
  | 'exit_before_start'
  | 'no_play_intent';

interface FeedVideoRecordFields {
  [key: string]: unknown;
}

/* ----- Kill-switch ---------------------------------------------------------
 * Driven by a dynamic config. Default false so nothing ships before the flag
 * resolves. Only *emission* is gated — trackers accumulate regardless, so a
 * summary emitted after the flag flips on mid-session still carries full data.
 */

let feedVideoDatadogEnabled = false;

export function setFeedVideoDatadogEnabled(enabled: boolean): void {
  feedVideoDatadogEnabled = enabled;
}

/* ----- Identity ------------------------------------------------------------ */

let cachedSessionId: string | null = null;
let idFallbackCounter = 0;

function generateFeedVideoId(): string {
  if (
    typeof crypto !== 'undefined' &&
    typeof crypto.randomUUID === 'function'
  ) {
    return crypto.randomUUID();
  }
  // Older iOS Safari (pre-15.4) / insecure contexts lack crypto.randomUUID. A
  // page-unique monotonic token is enough to correlate records within one page
  // load; global uniqueness is not required.
  idFallbackCounter += 1;
  return `f${idFallbackCounter}-${Math.round(nowMs())}`;
}

/** Stable id for the whole page load; ties every record of a session together. */
export function feedVideoSessionId(): string {
  if (cachedSessionId === null) cachedSessionId = generateFeedVideoId();
  return cachedSessionId;
}

/**
 * Cloudflare Stream playback id (first path segment of the manifest URL).
 * Null for non-Cloudflare sources; `src` is always present as the fallback facet.
 */
export function extractFeedVideoPlaybackId(src: string): string | null {
  try {
    const url = new URL(src);
    if (!url.hostname.endsWith('cloudflarestream.com')) return null;
    const segment = url.pathname.split('/')[1];
    return segment !== undefined && segment !== '' ? segment : null;
  } catch {
    return null;
  }
}

/* ----- Emission ------------------------------------------------------------ */

type FeedVideoRecordKey =
  | 'exp242.feedVideo.firstFrame'
  | 'exp242.feedVideo.loop'
  | 'exp242.feedVideo.error'
  | 'exp242.feedVideo.viewSummary';

const RECORD_MESSAGES: Record<FeedVideoRecordKey, string> = {
  'exp242.feedVideo.firstFrame': 'exp-242 feed video first frame',
  'exp242.feedVideo.loop': 'exp-242 feed video loop',
  'exp242.feedVideo.error': 'exp-242 feed video error',
  'exp242.feedVideo.viewSummary': 'exp-242 feed video view summary',
};

function emitFeedVideoRecord(
  key: FeedVideoRecordKey,
  fields: FeedVideoRecordFields,
  severity: 'info' | 'warn' | 'error' = 'info'
): void {
  if (!feedVideoDatadogEnabled) return;
  log(RECORD_MESSAGES[key], { key, ...fields }, severity);
}

/* ----- Clock ---------------------------------------------------------------- */

function nowMs(): number {
  if (
    typeof performance !== 'undefined' &&
    typeof performance.now === 'function'
  ) {
    return performance.now();
  }
  return Date.now();
}

function roundMs(value: number): number {
  return Number(value.toFixed(1));
}

/* ----- Error taxonomy ------------------------------------------------------- */

const MEDIA_ERROR_CLASS_BY_CODE: Record<number, FeedVideoErrorClass> = {
  1: 'media_aborted',
  2: 'media_network',
  3: 'media_decode',
  4: 'media_src_unsupported',
};

function classifyMediaErrorCode(code: number): FeedVideoErrorClass {
  return MEDIA_ERROR_CLASS_BY_CODE[code] ?? 'media_decode';
}

// hls.js ErrorTypes are stable string constants; matched literally so this
// module needs no hls.js import (keeps it trivially unit-testable).
function classifyHlsErrorType(hlsErrorType: string): FeedVideoErrorClass {
  if (hlsErrorType === 'networkError') return 'hls_network';
  if (hlsErrorType === 'mediaError') return 'hls_media';
  return 'hls_other';
}

interface FeedVideoHlsErrorInput {
  hlsErrorType: string;
  details: string;
  fatal: boolean;
}

/* ----- Page-hide flush registry ---------------------------------------------
 * A view can end without a React unmount (hard refresh, tab close, app
 * switch). Every live tracker registers its finalizer here; `pagehide` and
 * `visibilitychange: hidden` drain them so those views still emit exactly one
 * summary (finalize is idempotent). Listeners are installed once per page load
 * on first use and intentionally never removed — they are two cheap handlers
 * with page lifetime.
 */

const viewFinalizers = new Set<() => void>();
let pageFlushListenersInstalled = false;

function flushAllFeedVideoViews(): void {
  // Copy first: finalizers remove themselves from the set while iterating.
  for (const finalize of [...viewFinalizers]) {
    try {
      finalize();
    } catch {
      // Never let a flush error break page hide or mask other views' summaries.
    }
  }
}

function ensurePageFlushListeners(): void {
  if (pageFlushListenersInstalled) return;
  if (typeof window === 'undefined') return;
  pageFlushListenersInstalled = true;

  window.addEventListener('pagehide', flushAllFeedVideoViews);
  document.addEventListener('visibilitychange', () => {
    // Hidden is the last reliable moment before a kill that never fires
    // pagehide (iOS app swipe-away). Returning to the foreground starts a new
    // view via the provider's recovery nonce, so finalizing here is safe.
    if (document.visibilityState === 'hidden') flushAllFeedVideoViews();
  });
}

/* ----- View tracker ---------------------------------------------------------- */

interface FeedVideoViewTrackerOptions {
  videoElement: HTMLVideoElement;
  src: string;
  playbackPath: FeedVideoPlaybackPath;
  position: number;
}

export interface FeedVideoViewTracker {
  /** First call starts the TTFF clock and arms the startup-failure timeout. */
  onPlayIntent(): void;
  /** Play gating flipped off before a frame rendered; disarms the timeout. */
  onPlayIntentWithdrawn(): void;
  onPlayRejected(error: unknown): void;
  onHlsError(input: FeedVideoHlsErrorInput): void;
  /** Idempotent. Emits the view summary and detaches all listeners. */
  finalize(): void;
}

export function createFeedVideoViewTracker({
  videoElement,
  src,
  playbackPath,
  position,
}: FeedVideoViewTrackerOptions): FeedVideoViewTracker {
  ensurePageFlushListeners();

  const viewId = generateFeedVideoId();
  const activationAt = nowMs();

  const identity = {
    sessionId: feedVideoSessionId(),
    viewId,
    src,
    playbackId: extractFeedVideoPlaybackId(src),
    playbackPath,
    position,
  } as const;

  let finalized = false;

  // Mount time (activation -> loadedmetadata).
  let mountTimeMs: number | null = null;

  // TTFF.
  let playIntentAt: number | null = null;
  let hasFirstFrame = false;
  let ttffMs: number | null = null;
  let ttffSource: FeedVideoTtffSource | null = null;
  let firstFrameBeforeIntent = false;

  // Watch clock: accumulates only while (first frame rendered AND playing AND
  // document visible). "Playing" here means between a `playing` event and the
  // next pause-like event; a silent decoder freeze inside that window is a
  // documented overcount blind spot (no event exists to observe it).
  let isPlaying = false;
  let playingSince: number | null = null;
  let watchedAccumMs = 0;

  // Loop counting. Baselines only advance during presented playback (playing,
  // not seeking) so transitional samples — mid-seek positions, rebuffer
  // flushes, preload zeros — can neither fake a wrap nor swallow a real one
  // that straddles a buffering gap.
  let loopCount = 0;
  let lastTimeUpdateS: number | null = null;
  let rvfcSupported = false;
  let rvfcLoopCount = 0;
  let lastRvfcMediaTimeS: number | null = null;

  // Errors.
  let lastErrorClass: FeedVideoErrorClass | null = null;
  let lastErrorDetail: string | null = null;
  let hadFatalError = false;
  let startupTimedOut = false;
  let startupTimeoutId: ReturnType<typeof setTimeout> | null = null;

  function isDocumentVisible(): boolean {
    return (
      typeof document === 'undefined' || document.visibilityState === 'visible'
    );
  }

  function maybeStartWatchClock(): void {
    if (playingSince !== null) return;
    if (!hasFirstFrame || !isPlaying || !isDocumentVisible()) return;
    playingSince = nowMs();
  }

  function stopWatchClock(): void {
    if (playingSince === null) return;
    watchedAccumMs += nowMs() - playingSince;
    playingSince = null;
  }

  function clearStartupTimeout(): void {
    if (startupTimeoutId !== null) {
      clearTimeout(startupTimeoutId);
      startupTimeoutId = null;
    }
  }

  function recordError(
    errorClass: FeedVideoErrorClass,
    errorDetail: string | null,
    fatal: boolean
  ): void {
    if (finalized) return;
    // Summary keeps the most severe error: fatal beats non-fatal, latest wins
    // within the same severity.
    if (fatal || !hadFatalError) {
      lastErrorClass = errorClass;
      lastErrorDetail = errorDetail;
    }
    if (fatal) hadFatalError = true;

    emitFeedVideoRecord(
      'exp242.feedVideo.error',
      {
        ...identity,
        errorClass,
        errorDetail,
        fatal,
        // Minimal element snapshot for debugging error records only.
        readyState: videoElement.readyState,
        networkState: videoElement.networkState,
        currentTimeS: Number(videoElement.currentTime.toFixed(3)),
      },
      fatal ? 'error' : 'warn'
    );
  }

  function markFirstFrame(source: FeedVideoTtffSource): void {
    if (finalized || hasFirstFrame) return;
    hasFirstFrame = true;
    ttffSource = source;
    clearStartupTimeout();

    const timestamp = nowMs();
    if (playIntentAt === null) {
      // A frame composited before any play intent (e.g. Safari painting on
      // load). TTFF is clamped to 0 rather than reported negative or null.
      firstFrameBeforeIntent = true;
      ttffMs = 0;
    } else {
      ttffMs = roundMs(Math.max(0, timestamp - playIntentAt));
    }

    maybeStartWatchClock();
    emitFeedVideoRecord('exp242.feedVideo.firstFrame', {
      ...identity,
      ttffMs,
      ttffSource,
      firstFrameBeforeIntent,
      mountTimeMs,
    });
  }

  function minWrapPriorProgressS(): number {
    const { duration } = videoElement;
    const halfDuration =
      Number.isFinite(duration) && duration > 0 ? duration * 0.5 : 0;
    return Math.max(LOOP_WRAP_MIN_PRIOR_S, halfDuration);
  }

  function isLoopShapedJump(fromS: number | null, toS: number): boolean {
    return (
      fromS !== null &&
      toS < LOOP_WRAP_MAX_START_S &&
      fromS >= minWrapPriorProgressS()
    );
  }

  /* ----- Element listeners ----- */

  const handleLoadedMetadata = () => {
    if (mountTimeMs === null) {
      mountTimeMs = roundMs(nowMs() - activationAt);
    }
  };

  const handlePlaying = () => {
    isPlaying = true;
    // Without rVFC, `playing` is the primary first-frame heuristic. With rVFC
    // available, only a real composited frame counts.
    if (!rvfcSupported) markFirstFrame('heuristic');
    maybeStartWatchClock();
  };

  const handlePauseLike = () => {
    isPlaying = false;
    stopWatchClock();
  };

  // `waiting` means playback actually halted for data (a `playing` event is
  // guaranteed on resume, restarting the clock). `stalled` is deliberately NOT
  // handled: it only signals the network fetch stalling while playback often
  // continues from buffer, and no `playing` event follows — stopping the clock
  // there would permanently undercount watch time.
  const handleWaiting = () => {
    isPlaying = false;
    stopWatchClock();
  };

  const handleTimeUpdate = () => {
    const currentTimeS = videoElement.currentTime;

    // currentTime advancing past zero proves playback progressed even if the
    // `playing` event was missed (old-Safari path only; rVFC owns this signal
    // everywhere else).
    if (!rvfcSupported && currentTimeS > 0) markFirstFrame('heuristic');

    // Mid-seek samples are transitional, not presented playback: they must not
    // count as wraps, and must not advance the baseline either — a loop wrap
    // that surfaces as seek-to-start still has to compare against the pre-wrap
    // position once the seek completes.
    if (videoElement.seeking) return;
    if (!hasFirstFrame || !isPlaying) return;

    if (isLoopShapedJump(lastTimeUpdateS, currentTimeS)) {
      loopCount += 1;
      emitFeedVideoRecord('exp242.feedVideo.loop', {
        ...identity,
        loopCount,
        loopSource: 'timeupdate',
      });
    }

    lastTimeUpdateS = currentTimeS;
  };

  const handleElementError = () => {
    const mediaError = videoElement.error;
    recordError(
      classifyMediaErrorCode(mediaError?.code ?? 0),
      mediaError?.message ?? null,
      true
    );
  };

  const handleVisibilityChange = () => {
    if (isDocumentVisible()) {
      maybeStartWatchClock();
    } else {
      stopWatchClock();
    }
  };

  const pauseLikeEvents = ['pause', 'emptied', 'ended'] as const;

  videoElement.addEventListener('loadedmetadata', handleLoadedMetadata);
  videoElement.addEventListener('playing', handlePlaying);
  videoElement.addEventListener('timeupdate', handleTimeUpdate);
  videoElement.addEventListener('error', handleElementError);
  videoElement.addEventListener('waiting', handleWaiting);
  for (const eventType of pauseLikeEvents) {
    videoElement.addEventListener(eventType, handlePauseLike);
  }
  document.addEventListener('visibilitychange', handleVisibilityChange);

  /* ----- requestVideoFrameCallback chain ----- */

  rvfcSupported = typeof videoElement.requestVideoFrameCallback === 'function';

  if (rvfcSupported) {
    const onVideoFrame: VideoFrameRequestCallback = (_now, metadata) => {
      if (finalized) return;

      markFirstFrame('rvfc');

      // Frame-accurate loop cross-check. rVFC only fires while frames are
      // presented, so paused/hidden states cannot produce false wraps.
      const mediaTimeS = metadata.mediaTime;
      if (isLoopShapedJump(lastRvfcMediaTimeS, mediaTimeS)) {
        rvfcLoopCount += 1;
      }
      lastRvfcMediaTimeS = mediaTimeS;

      videoElement.requestVideoFrameCallback(onVideoFrame);
    };
    videoElement.requestVideoFrameCallback(onVideoFrame);
  }

  /* ----- Summary ----- */

  function deriveOutcome(): FeedVideoOutcome {
    // Play intent is checked before first frame: the activation window
    // pre-buffers cards ahead of the viewport, so a frame often composites for
    // a card the user never reaches. That view is `no_play_intent`, not
    // `success` — outcome describes the user-facing story, not decoder state.
    if (playIntentAt === null) {
      return hadFatalError ? 'playback_failure' : 'no_play_intent';
    }
    if (hasFirstFrame) {
      return hadFatalError ? 'playback_failure' : 'success';
    }
    if (startupTimedOut) return 'startup_failure';
    return 'exit_before_start';
  }

  function finalize(): void {
    if (finalized) return;
    finalized = true;

    stopWatchClock();
    clearStartupTimeout();

    videoElement.removeEventListener('loadedmetadata', handleLoadedMetadata);
    videoElement.removeEventListener('playing', handlePlaying);
    videoElement.removeEventListener('timeupdate', handleTimeUpdate);
    videoElement.removeEventListener('error', handleElementError);
    videoElement.removeEventListener('waiting', handleWaiting);
    for (const eventType of pauseLikeEvents) {
      videoElement.removeEventListener(eventType, handlePauseLike);
    }
    document.removeEventListener('visibilitychange', handleVisibilityChange);
    viewFinalizers.delete(finalize);

    const { duration } = videoElement;
    emitFeedVideoRecord('exp242.feedVideo.viewSummary', {
      ...identity,
      outcome: deriveOutcome(),
      ttffMs,
      ttffSource,
      firstFrameBeforeIntent,
      mountTimeMs,
      watchedMs: roundMs(watchedAccumMs),
      clipDurationS:
        Number.isFinite(duration) && duration > 0
          ? Number(duration.toFixed(3))
          : null,
      loopCount,
      loopSource: 'timeupdate',
      rvfcLoopCount: rvfcSupported ? rvfcLoopCount : null,
      loopCountDivergent: rvfcSupported && rvfcLoopCount !== loopCount,
      errorClass: lastErrorClass,
      errorDetail: lastErrorDetail,
      hadFatalError,
    });
  }

  viewFinalizers.add(finalize);

  return {
    onPlayIntent() {
      if (finalized) return;
      // Latest intent wins: a pause/resume cycle before any frame restarts the
      // wait the user actually perceives, so TTFF must not include the paused gap.
      playIntentAt = nowMs();
      if (hasFirstFrame) return;

      // Video Startup Failure: intent but no frame within the timeout. This is
      // the grey-video signature — invisible to every error API.
      clearStartupTimeout();
      startupTimeoutId = setTimeout(() => {
        startupTimeoutId = null;
        if (finalized || hasFirstFrame) return;
        startupTimedOut = true;
        recordError(
          'startup_timeout',
          `no first frame within ${VIDEO_STARTUP_FAILURE_TIMEOUT_MS}ms`,
          true
        );
      }, VIDEO_STARTUP_FAILURE_TIMEOUT_MS);
    },
    onPlayIntentWithdrawn() {
      // Deliberate pause before first frame is not a startup failure.
      clearStartupTimeout();
    },
    onPlayRejected(error: unknown) {
      const detail =
        error instanceof Error
          ? `${error.name}: ${error.message}`
          : String(error);
      recordError('play_rejected', detail, false);
    },
    onHlsError({ hlsErrorType, details, fatal }: FeedVideoHlsErrorInput) {
      recordError(classifyHlsErrorType(hlsErrorType), details, fatal);
    },
    finalize,
  };
}
